/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Translations Sagas
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */

import set from 'lodash/set';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { requestSaga } from '../../sagas/requestSaga';
import { API_INTERNATIONALIZATION_FROM_ZK } from '../app/Api';
import { makeSelectAppFeatureEnabled, makeSelectOperatorSettings } from '../app/appSelectors';
import { EAppFeatures } from '../app/appTypes';
import {
    LOAD_EXTERNAL_TRANSLATIONS_START,
    LOAD_INTERNAL_TRANSLATIONS_START,
    TranslationFileNamesType,
} from './constants';
import {
    loadExternalTranslationsFailure,
    loadExternalTranslationsFinish,
    loadExternalTranslationsSuccess,
    loadInternalTranslationsFail,
    loadInternalTranslationsSuccess,
} from './intActions';
import { makeSelectCustomConfig, makeSelectLocale } from './intSelectors';
import { updateTranslateParams } from './parseTranslation';
import type { ILoadTranslation } from './types';

const requesOption = {
    headers: {
        cache: 'no-cache',
    },
};

export function* loadExternalTranslationsStartSaga(): SagaIterator {
    try {
        const operatorSettings = yield select(makeSelectOperatorSettings());
        const locale = yield select(makeSelectLocale());
        let localizationFromTheme = {};
        let localizationFromCustomFile = {};
        let localizationFromZK = {};

        try {
            const generalUrl = `/theme/translations/${locale}/general.json`;
            localizationFromTheme = yield call(requestSaga, generalUrl, 'GET', null, requesOption);
        } catch (e) {
            // skip this file
        }

        try {
            const customUrl = `/theme/translations/${locale}/custom.json`;
            localizationFromCustomFile = yield call(requestSaga, customUrl, 'GET', null, requesOption);
        } catch (e) {
            // skip this file
        }

        const internationalization = yield select(makeSelectAppFeatureEnabled(EAppFeatures.internationalization));
        if (internationalization) {
            try {
                const data: { json: string } = yield call(requestSaga, API_INTERNATIONALIZATION_FROM_ZK, 'GET', null, requesOption);
                localizationFromZK = JSON.parse(data.json)
            } catch (e) {
                // skip this file
            }
        }

        yield put(loadExternalTranslationsSuccess(
            updateTranslateParams(operatorSettings,
                { ...localizationFromTheme, ...localizationFromCustomFile, ...localizationFromZK })));
    } catch (error: any) {
        yield put(loadExternalTranslationsFailure(error));
    } finally {
        yield put(loadExternalTranslationsFinish());
    }
}

export function* loadInternalLocaleSaga({ payload }:
                                            { payload: TranslationFileNamesType[]; type: string }): SagaIterator {
    const locale = yield select(makeSelectLocale());
    try {
        const genTranslations: any = {};

        payload.forEach((name: TranslationFileNamesType) => {
            return genTranslations[name] = call(
                requestSaga,
                `/translations/${locale}/${name}.json`,
                'GET', null,
            );
        });

        const customConfig: { [key: string]: string } = yield select(makeSelectCustomConfig());
        const translations: ILoadTranslation = yield all(genTranslations);

        if (customConfig) {
            Object.keys(customConfig).forEach((path: string) => {
                const isCustomIncludesCurrentTranslation = payload.find(fileName => path.startsWith(fileName))
                if (isCustomIncludesCurrentTranslation) {
                    set(translations, path, customConfig[path]);
                }
            });
        }

        try {
            const themePageTranslations: any = {};
            for (const i in payload) {
                const name = payload[i];
                try {
                    themePageTranslations[name] = yield call(
                        requestSaga,
                        `/theme/translations/${locale}/${name}.json`,
                        'GET', null,
                        { throwIfHasError: false },
                    );
                } catch (err) {
                    console.error(err);
                }
            }
            for (const themePageFileName in themePageTranslations) {
                if (Object.prototype.hasOwnProperty.call(themePageTranslations, themePageFileName)
                    && themePageTranslations[themePageFileName]) {
                    set(translations, themePageFileName, themePageTranslations[themePageFileName]);
                }
            }
        } catch (err) {
            console.error(err);
        }

        yield put(loadInternalTranslationsSuccess(translations));
    } catch (error: any) {
        yield put(loadInternalTranslationsFail(error));
    }
}

export function* intSagas(): SagaIterator {
    yield takeEvery(LOAD_INTERNAL_TRANSLATIONS_START, loadInternalLocaleSaga);
    yield takeEvery(LOAD_EXTERNAL_TRANSLATIONS_START, loadExternalTranslationsStartSaga);
}
